/** @format */

/* eslint require-await: "warn" */
import { AxiosRequestConfig, AxiosResponse } from 'axios';
import { TFunction } from 'i18next';

import { User } from '@toucan/common/services/user/types';

import {
    FormValues,
    client,
    isAxiosValidationError,
} from '@toucan/client/global/axios';
import {
    AvailableMarket,
    Market,
    MarketCode,
} from '@toucan/client/global/market';
import { Model } from '@toucan/client/global/model';
import {
    SavedProduct,
    transformSavedProduct,
} from '@toucan/client/global/product';
import { Address, AddressWithId } from '@toucan/client/global/transaction';

import { authClient } from '../auth-client';
import {
    CustomerPhoneNumber,
    CustomerPhoneNumberResponse,
    PersonalDetails,
} from '../config/types';
import {
    transformAddressValidationErrors,
    transformInboundAddressData,
    transformOutboundAddressData,
} from '../helpers/address';

/**
 * Call the sign up API with the given form values
 *
 * @param values {FormValues} the user details to sign up with
 * @param market {MarketCode} the market code
 */
export async function signUp(
    values: FormValues,
    market: MarketCode,
    csrfToken: string
): Promise<AxiosResponse<{ providedEmail: string }>> {
    const config: AxiosRequestConfig = {
        headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
            'X-Market': market.toLowerCase(),
            'CSRF-Token': csrfToken,
        },
    };

    return client.post('/public-api/v3/user/signup/', values, config);
}

/**
 * Quick sign up still uses the confirm password field.
 * This is likely to be removed in the future due to balance work.
 */
export async function quickSignUp(
    values: FormValues,
    market: MarketCode,
    csrfToken: string
): Promise<AxiosResponse<{ providedEmail: string }>> {
    const config: AxiosRequestConfig = {
        headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
            'X-Market': market.toLowerCase(),
            'CSRF-Token': csrfToken,
        },
    };

    return client.post('/public-api/v2/user/signup/', values, config);
}

export function sendVerification(
    email: string,
    market: MarketCode
): Promise<void> {
    return authClient.sendVerification(email, market);
}

export function verify(hash: string, market: MarketCode): Promise<void> {
    return authClient.verify(hash, market);
}

/**
 * Separate the email verification hash from the current
 * window location path and return them as a tuple
 */
export function getEmailVerifyPathAndHash(): [string, string] {
    const pathArray = window.location.pathname.split('/');
    const hash = pathArray[4];
    pathArray.splice(pathArray.length - 1, 1);

    return [pathArray.join('/'), hash];
}

/**
 * Get a new anonymous JWT token.
 *
 * The token can be used with the basket API if we don't have a logged in token
 * @param market {MarketCode} the market code
 */
export async function getAnonymousToken(market: MarketCode): Promise<void> {
    const config: AxiosRequestConfig = {
        headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
            'X-Market': market.toLowerCase(),
        },
    };

    return client.post('/public-api/v2/jwt/new/', {}, config);
}

/**
 * Upgrades a token to a JWT
 * @param token A token to upgrade to a jwt
 * @param market {MarketCode} the market code
 */
export function upgradeAuthLinkToJWT(
    token: string,
    market: MarketCode
): Promise<void> {
    const config: AxiosRequestConfig = {
        headers: {
            Authorization: `AuthLink ${token}`,
            'X-Market': market.toLowerCase(),
        },
    };

    return client.post('/public-api/v2/jwt/upgrade/', {}, config);
}

export function login(
    values: FormValues,
    market: MarketCode,
    csrfToken: string
): Promise<void> {
    return authClient.login(values, market, csrfToken);
}

export function logout(): Promise<void> {
    return authClient.logout();
}

export const getUser = (market: MarketCode): Promise<User> =>
    authClient.getUser(market);

/**
 * Gets the password reset hash from the current window location path
 *
 * @returns {string} The password reset hash
 */
export function getPasswordResetHash(): string {
    const pathArray = window.location.pathname.split('/');
    return pathArray[4];
}

export function isPasswordResetHashValid(
    hash: string,
    market: MarketCode
): Promise<void> {
    return authClient.isPasswordResetHashValid(hash, market);
}

export function resetPassword(
    hash: string,
    password: string,
    confirmPassword: string,
    market: MarketCode
): Promise<void> {
    return authClient.resetPassword(hash, password, confirmPassword, market);
}

/**
 * Gets the address book for the currently logged in user
 *
 * @param market {MarketCode} the market code
 */
export async function getAddressBook(
    market: MarketCode
): Promise<AddressWithId[]> {
    const config: AxiosRequestConfig = {
        headers: {
            'X-Market': market.toLowerCase(),
        },
    };
    const response = await client.get(
        '/public-api/v2/user/address-book/',
        config
    );

    return response.data.map(transformInboundAddressData);
}

/**
 * Add an address to the currently logged in user
 *
 * @param address the address to add
 * @param market {MarketCode} the market code
 * @returns the address id
 */
export async function addAddress(
    address: Address,
    market: MarketCode
): Promise<number> {
    const config: AxiosRequestConfig = {
        headers: {
            'X-Market': market.toLowerCase(),
        },
    };
    try {
        const response = await client.post(
            `/public-api/v2/user/add-address/`,
            transformOutboundAddressData(address),
            config
        );

        return response.data.addressId;
    } catch (error: unknown) {
        if (isAxiosValidationError(error)) {
            error.validationErrors = transformAddressValidationErrors(
                error.validationErrors
            );
        }
        throw error;
    }
}

export async function updateAddress(
    address: AddressWithId,
    market: MarketCode
): Promise<AddressWithId> {
    const config: AxiosRequestConfig = {
        headers: {
            'X-Market': market.toLowerCase(),
        },
    };

    try {
        const response = await client.put(
            `/public-api/v1/user/address-book/${address.id}/`,
            transformOutboundAddressData(address),
            config
        );

        return response.data;
    } catch (error: unknown) {
        if (isAxiosValidationError(error)) {
            error.validationErrors = transformAddressValidationErrors(
                error.validationErrors
            );
        }
        throw error;
    }
}

export function verifyCustomerEmail(
    emailAddress: string,
    market: MarketCode
): Promise<boolean> {
    return authClient.verifyCustomerEmail(emailAddress, market);
}

/**
 * Gets the saved products for a user
 * @async
 * @returns {Product[]} An array of products saved by the user
 */
export async function getSavedProducts(
    market: Market,
    availableMarkets: AvailableMarket[],
    t: TFunction
): Promise<SavedProduct[]> {
    const config: AxiosRequestConfig = {
        headers: {
            'X-Market': market.code.toLowerCase(),
        },
    };
    const response = await client.get(
        '/public-api/v2/user/favourite/product/',
        config
    );

    return response.data.map(({ product }: { product: SavedProduct }) =>
        transformSavedProduct(product, market, availableMarkets, t)
    );
}

/**
 * Request to handle saving a product to the users saved products in their account.
 *
 * @async
 * @param market {MarketCode} the market code
 * @param id Product id
 * @returns {AxiosResponse} A promise that resolves with a response from the api
 */
export async function addSavedProduct(
    market: Market,
    availableMarkets: AvailableMarket[],
    id: number,
    t: TFunction
): Promise<SavedProduct | undefined> {
    const config: AxiosRequestConfig = {
        headers: {
            'X-Market': market.code.toLowerCase(),
        },
    };

    const response = await client.post(
        `/public-api/v2/user/favourite/product/add/${id}/`,
        config
    );

    const product: SavedProduct = response?.data?.product;
    if (!product) {
        return;
    }

    return transformSavedProduct(product, market, availableMarkets, t);
}

/**
 * Request to handle removing a saved product.
 *
 * @async
 * @param market {MarketCode} the market code
 * @param id Product id
 * @returns {AxiosResponse} A promise that resolves with a response from the api
 */
export async function removeSavedProduct(
    market: MarketCode,
    id: number
): Promise<AxiosResponse> {
    const config: AxiosRequestConfig = {
        headers: {
            'X-Market': market.toLowerCase(),
        },
    };

    return client.post(
        `/public-api/v2/user/favourite/product/delete/${id}/`,
        config
    );
}

export enum UpdateMarketingPreferenceChoice {
    EMAIL = 'receive_offers_via_email',
    TEXT = 'receive_offers_via_text',
}

/**
 * Updates a users marketing preference
 *
 * @param pref The marketing preference to update
 * @param value If the user has opted in to this choice
 */
export async function updateMarketingPreference(
    preference: UpdateMarketingPreferenceChoice,
    value: boolean
) {
    const data = { value };
    const config: AxiosRequestConfig = {
        headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
        },
    };

    return client.post(
        `/public-api/v1/user/set-preference/${preference}/`,
        data,
        config
    );
}

/**
 * Gets the saved models for a user
 * @async
 * @returns {Promise<Model[]>} An array of Models saved by the user
 */
export async function getSavedModels(market: Market): Promise<Model[]> {
    const config: AxiosRequestConfig = {
        headers: {
            'X-Market': market.code.toLowerCase(),
        },
    };
    const response = await client.get(
        '/public-api/v2/user/favourite/model/',
        config
    );

    return response.data.map(({ model }: any) => model);
}

/**
 * Request to handle saving a model to the users saved models in their account.
 *
 * @async
 * @param market {MarketCode} the market code
 * @param id {number} Model id
 * @returns {AxiosResponse} A promise that resolves with a response from the api
 */
export async function addSavedModel(
    market: MarketCode,
    id: number
): Promise<AxiosResponse> {
    const config: AxiosRequestConfig = {
        headers: {
            'X-Market': market.toLowerCase(),
        },
    };

    return client.post(
        `/public-api/v2/user/favourite/model/add/${id}/`,
        config
    );
}

/**
 * Request to handle removing a saved model.
 *
 * @async
 * @param market {MarketCode} the market code
 * @param id {number} Model id
 * @returns @returns {AxiosResponse} A promise that resolves with a response from the api
 */
export async function removeSavedModel(
    market: MarketCode,
    id: number
): Promise<AxiosResponse> {
    const config: AxiosRequestConfig = {
        headers: {
            'X-Market': market.toLowerCase(),
        },
    };

    return client.post(
        `/public-api/v2/user/favourite/model/delete/${id}/`,
        config
    );
}

export function updatePersonalInformation(
    customerId: number,
    personalDetails: PersonalDetails,
    market: MarketCode
): Promise<void> {
    return authClient.updatePersonalInformation(
        customerId,
        personalDetails,
        market
    );
}

export function updatePassword(
    password: string,
    newPassword: string,
    customerId: number,
    market: MarketCode
): Promise<void> {
    return authClient.updatePassword(password, newPassword, customerId, market);
}

/**
 * Call out to GeoLocate API
 */
export function geolocateUser(): Promise<
    AxiosResponse<{ userMarket: string }>
> {
    return client.get('/geolocate');
}

/**
 * Gets a list of phone numbers for the given customerId
 * This will default to 50 phone numbers, we will not display more than
 * 50 numbers for MVP, so we will need to handle this postMVP
 *
 * @param market {MarketCode} the market code
 * @param market {customerId} the customerId
 */
export async function getUserPhoneNumbers(
    customerId: number,
    market: MarketCode
): Promise<CustomerPhoneNumberResponse> {
    const config: AxiosRequestConfig = {
        headers: {
            'X-Market': market.toLowerCase(),
        },
    };
    const response = await client.get(
        `/public-api/customer-service/v1/customer/${customerId}/personal-information/phone-numbers/`,
        config
    );

    return response.data;
}

/**
 * Deletes a phone number for the given customerId
 *
 * @param market {MarketCode} the market code
 * @param market {customerId} the customerId
 */
export async function deletePhoneNumber(
    customerId: number,
    phoneNumberId: number,
    market: MarketCode
): Promise<boolean> {
    const config: AxiosRequestConfig = {
        headers: {
            'X-Market': market.toLowerCase(),
        },
    };

    const response = await client.delete(
        `/public-api/customer-service/v1/customer/${customerId}/personal-information/phone-numbers/${phoneNumberId}/`,
        config
    );

    return response.data;
}

/**
 * Creates a phone number for the given customerId
 *
 * @param market {MarketCode} the market code
 * @param market {customerId} the customerId
 */
export async function createPhoneNumber(
    customerId: number,
    phoneNumber: string,
    market: MarketCode
): Promise<CustomerPhoneNumberResponse> {
    const config: AxiosRequestConfig = {
        headers: {
            'X-Market': market.toLowerCase(),
        },
    };

    const response = await client.post(
        `/public-api/customer-service/v1/customer/${customerId}/personal-information/phone-numbers/`,
        { phoneNumber },
        config
    );

    return response.data;
}

/**
 * Updates a phone number for the given customerId
 *
 * @param market {MarketCode} the market code
 * @param market {customerId} the customerId
 */
export async function updatePhoneNumber(
    customerId: number,
    { phoneNumber, id }: CustomerPhoneNumber,
    market: MarketCode
): Promise<CustomerPhoneNumberResponse> {
    const config: AxiosRequestConfig = {
        headers: {
            'X-Market': market.toLowerCase(),
        },
    };

    const response = await client.put(
        `/public-api/customer-service/v1/customer/${customerId}/personal-information/phone-numbers/${id}/`,
        { phoneNumber },
        config
    );

    return response.data;
}
